import React, {useCallback, useEffect, useState} from "react";
import Label from "../../ui/components/Label/Label";
import useTranslate from "../../core/languages/hooks/useTranslate";
import useCustomerContext from "../../customers/hooks/useCustomerContext";
import {LockClosedIcon} from "@heroicons/react/24/outline";
import usePaycometToken from "../hooks/usePaycometToken";
import usePaymentCardSave from "../../payment/hooks/usePaymentCardSave";
import {PaymentCardResult} from "../types/PaymentCard";
import usePaycometJetId from "../hooks/usePaycometJetId";

// Declare Paycomet global type
declare global {
    interface Window {
        Paycomet?: any;
    }
}

// ─── Paycomet script loader ───────────────────────────────────────────────────

const PAYCOMET_SCRIPT_ID = "paycomet-jetiframe-script";

function loadPaycometScript(lang: string): Promise<void> {
    return new Promise((resolve) => {
        // Eliminar script existente si Paycomet no está disponible
        const existingScript = document.getElementById(PAYCOMET_SCRIPT_ID);
        if (existingScript && !window.Paycomet) {
            existingScript.remove();
            delete window.Paycomet;
        }

        // Si Paycomet ya está disponible, resolver inmediatamente
        if (window.Paycomet) {
            resolve();
            return;
        }

        // Crear nuevo script con cache buster
        const script = document.createElement("script");
        script.id = PAYCOMET_SCRIPT_ID;
        script.src = `https://api.paycomet.com/gateway/paycomet.jetiframe.js?lang=${lang}&t=${Date.now()}`;
        script.async = true;

        script.onload = () => {
            // Esperar a que Paycomet se inicialice (máximo 5 segundos)
            let attempts = 0;
            const maxAttempts = 50;

            const checkPaycomet = setInterval(() => {
                attempts++;
                if (window.Paycomet) {
                    clearInterval(checkPaycomet);
                    resolve();
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkPaycomet);
                    resolve();
                }
            }, 100);
        };

        script.onerror = () => {
            resolve();
        };

        document.head.appendChild(script);
    });
}

// Clases compartidas para que los contenedores de iframe tengan el mismo aspecto
// que los inputs del proyecto
const iframeContainerClass =
    "mt-1 h-11 w-full rounded-2xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900";

// Estilo que Paycomet inyecta dentro del iframe para que el <input> interno
// coincida visualmente con el contenedor (sin borde propio, altura y fuente iguales)
const iframeInnerStyle =
    "width:100%; height:44px; font-size:14px; font-family:inherit; padding-left:16px; padding-right:16px; border:0; outline:none; background:transparent;";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreditCardPaycometFormProps {
    /** Texto del botón de submit. Por defecto usa la clave "bookOk.payNow" */
    submitLabel?: string;
    /** Callback cuando la tarjeta se guarda con éxito en el backend */
    onSuccess?: (result: PaymentCardResult) => void;
    /** Callback cuando ocurre un error al guardar la tarjeta */
    onError?: (err: unknown) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const CreditCardPaycometForm: React.FC<CreditCardPaycometFormProps> = ({
    submitLabel,
    onSuccess,
    onError,
}) => {
    const t = useTranslate();
    const {languageCode} = useCustomerContext();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const {data: jetIdData, isLoading: isLoadingJetId} = usePaycometJetId();

    useEffect(() => {
        loadPaycometScript(languageCode ?? "es");
    }, [languageCode]);

    const savePaymentCard = usePaymentCardSave();

    const handleToken = useCallback((token: string) => {
        // Limpiar mensaje de error previo
        setErrorMessage("");

        savePaymentCard({ token: token })
            .then(result => {
                onSuccess?.(result);
            })
            .catch(err => {
                // Extraer mensaje de error del servidor
                let errorMsg = t("booking.paymentError") || "Error al procesar el pago";

                if (err?.response?.data?.message) {
                    errorMsg = err.response.data.message;
                } else if (err?.message) {
                    errorMsg = err.message;
                } else if (typeof err === 'string') {
                    errorMsg = err;
                }

                setErrorMessage(errorMsg);
                onError?.(err);
            });
    }, [savePaymentCard, onSuccess, onError, t]);

    usePaycometToken(handleToken);

    // Reinicializar Paycomet cuando el componente se monta
    useEffect(() => {
        const panElement = document.getElementById("paycomet-pan");
        const cvcElement = document.getElementById("paycomet-cvc2");

        // Limpiar iframes anteriores
        if (panElement) panElement.innerHTML = "";
        if (cvcElement) cvcElement.innerHTML = "";

        let cancelled = false;

        // Cargar script y luego inicializar Paycomet
        loadPaycometScript(languageCode ?? "es").then(() => {
            if (cancelled) return;

            if (window.Paycomet && typeof window.Paycomet.init === 'function') {
                try {
                    window.Paycomet.init();
                } catch (err) {
                }
            }
        });

        // Cleanup al desmontar
        return () => {
            cancelled = true;
            if (panElement) panElement.innerHTML = "";
            if (cvcElement) cvcElement.innerHTML = "";
        };
    }, [languageCode]);

    return (
        // Sin onSubmit propio: Paycomet añade su propio addEventListener("submit")
        // que llama a e.preventDefault() + prepareSubmit() y gestiona el flujo completo.
        <form id="paycometPaymentForm">
            {/* jetID oculto — Paycomet lo lee para identificar al comercio */}
            <input type="hidden" data-paycomet="jetID" value={jetIdData?.jetId || ""}/>

            <div className="space-y-4">

                {/* ── Titular ── */}
                <div>
                    <Label>{t("booking.creditCardHolder")}</Label>
                    <input
                        type="text"
                        data-paycomet="cardHolderName"
                        placeholder="John Doe"
                        className="mt-1 h-11 w-full rounded-2xl border border-neutral-200 bg-white px-4 text-sm font-normal focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 dark:border-neutral-700 dark:bg-neutral-900"
                    />
                </div>

                {/* ── Número de tarjeta ── */}
                <div>
                    <Label>{t("booking.creditCardNumber")}</Label>
                    {/* Paycomet inyecta el iframe dentro de este div */}
                    <div id="paycomet-pan"
                         className="mt-1 h-11 w-full rounded-2xl border border-neutral-200 bg-white px-4 text-sm font-normal focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 dark:border-neutral-700 dark:bg-neutral-900"
                    ></div>
                    <input
                        paycomet-style="width: 100%; height: 44px; font-size:14px; padding-left:16px; padding-right:16px; border:0px;"
                        paycomet-name="pan"
                    />
                </div>

                {/* ── CVV + Caducidad ── */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>CVV / CVC</Label>
                        {/* Paycomet inyecta el iframe dentro de este div */}
                        <div id="paycomet-cvc2" className={iframeContainerClass} style={{overflow: "hidden"}}/>
                        {/* paycomet-style estiliza el <input> dentro del iframe */}
                        <input paycomet-name="cvc2" type="hidden" paycomet-style={iframeInnerStyle}/>
                    </div>
                    <div>
                        <Label>{t("booking.expiration")}</Label>
                        <input
                            type="text"
                            placeholder="MM/YY"
                            maxLength={5}
                            className="mt-1 h-11 w-full rounded-2xl border border-neutral-200 bg-white px-4 text-sm font-normal focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 dark:border-neutral-700 dark:bg-neutral-900"
                            onChange={(e) => {
                                const input = e.target;
                                let value = input.value.replace(/\D/g, ''); // Solo dígitos

                                // Formatear como MM/YY
                                if (value.length >= 2) {
                                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                }

                                input.value = value;

                                // Extraer mes y año para los campos ocultos
                                const parts = value.split('/');
                                if (parts.length === 2) {
                                    const monthInput = document.querySelector('[data-paycomet="dateMonth"]') as HTMLInputElement;
                                    const yearInput = document.querySelector('[data-paycomet="dateYear"]') as HTMLInputElement;
                                    if (monthInput) monthInput.value = parts[0];
                                    if (yearInput) yearInput.value = parts[1];
                                }
                            }}
                            onKeyDown={(e) => {
                                // Mejorar el comportamiento del backspace
                                if (e.key === 'Backspace') {
                                    const input = e.target as HTMLInputElement;
                                    const cursorPos = input.selectionStart || 0;
                                    // Si el cursor está justo después del /, eliminar también el dígito anterior
                                    if (cursorPos === 3 && input.value[2] === '/') {
                                        e.preventDefault();
                                        input.value = input.value.slice(0, 1) + input.value.slice(3);
                                        input.setSelectionRange(1, 1);
                                    }
                                }
                            }}
                        />
                        {/* Campos ocultos para Paycomet */}
                        <input type="hidden" data-paycomet="dateMonth" />
                        <input type="hidden" data-paycomet="dateYear" />
                    </div>
                </div>
            </div>

            {/* Zona de errores de Paycomet */}
            <div id="paymentErrorMsg" className="mt-3 text-sm text-red-600 dark:text-red-400">
                {errorMessage && <p>{errorMessage}</p>}
            </div>

            {/* ── Botón pay — type submit para que Paycomet lo intercepte ── */}
            <button
                type="submit"
                disabled={isLoadingJetId || !jetIdData?.jetId}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-primary-6000 px-6 py-3.5 text-base font-medium text-white shadow-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
                <LockClosedIcon className="h-4 w-4"/>
                {isLoadingJetId ? t("common.loading") || "Cargando..." : submitLabel ?? t("bookOk.payNow")}
            </button>
        </form>
    );
};

export default CreditCardPaycometForm;
